import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import InputCst from "@components/InputCst/InputCst";
import { LanguageSelectorProps } from "@components/InputCst/LanguageSelector/types";
import { ServicesURL } from "@utils/utilsApi";
import useSWRMutation from "swr/mutation";
import { MethodType } from "../../../../services/type";
import { fetcher } from "../../../../services/config";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import InputWrapper from "@components/InputCst/InputWrapper";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const LanguageSelectorLinks: React.FC<LanguageSelectorProps> = ({
  languages,
  col,
  id,
  idObject,
}) => {
  const { t, i18n } = useTranslation(["form"]);
  const [isLangPopupOpen, setLangPopupOpen] = useState(false);
  const [submitState, setSubmitState] = useState<{
    loading: boolean;
    submitted: boolean;
  }>({ loading: false, submitted: false });
  const [fieldStates, setFieldStates] = useState<Record<any, any>>({});

  const { trigger } = useSWRMutation(
    ServicesURL.postTranslations,
    (url: string, arg: { arg: any }) =>
      fetcher(`${url}`, MethodType.PUT, arg.arg),
  );

  const handleLangIconClick = async () => {
    setLangPopupOpen(true);
    setFieldStates({});

    try {
      // GET `url_label`
      const urlLabelResponse = await fetcher(
        `${ServicesURL.getTranslations}?idObject=${idObject}&corporateId=${id}&entityType=useful_links&propertyName=url_label`,
        MethodType.GET,
      );

      // GET `description`
      const descriptionResponse = await fetcher(
        `${ServicesURL.getTranslations}?idObject=${idObject}&corporateId=${id}&entityType=useful_links&propertyName=description`,
        MethodType.GET,
      );

      // Combine responses
      const combinedTranslations = combineResponses(
        urlLabelResponse.propertiesTranslated,
        descriptionResponse.propertiesTranslated,
      );

      setFieldStates(combinedTranslations);
    } catch (error) {
      console.error("Error fetching translations:", error);
    }
  };

  const combineResponses = (
    urlLabelTranslations: Record<string, any>,
    descriptionTranslations: Record<string, any>,
  ): Record<string, any> => {
    const combined: Record<string, any> = {};

    Object.keys(urlLabelTranslations).forEach((lang) => {
      combined[lang] = [
        urlLabelTranslations[lang],
        descriptionTranslations[lang],
      ];
    });

    return combined;
  };

  const handleLangPopupClose = () => {
    setLangPopupOpen(false);
  };

  const handleFieldChange = (langId: string, value: string, field: string) => {
    setFieldStates((prev: any) => {
      const fieldData = prev[langId] || [];
      const updatedFieldData = Array.isArray(fieldData)
        ? fieldData.map((item: any) =>
            item.propertyName === field
              ? { ...item, translatedValue: value }
              : item,
          )
        : fieldData;

      return {
        ...prev,
        [langId]: updatedFieldData,
      };
    });
  };

  const handleSubmit = async () => {
    //DIVIDE PROPERTIES
    const descriptions = Object.entries(fieldStates).reduce(
      (acc, [lang, fields]) => {
        const field_url_description = fields.find(
          (item: any) => item.propertyName === "description",
        );
        if (field_url_description) {
          acc[lang] = {
            propertyName: "description",
            translatedValue: field_url_description.translatedValue,
          };
        }
        return acc;
      },
      {} as Record<string, { propertyName: string; translatedValue: string }>,
    );

    const urlLabels = Object.entries(fieldStates).reduce(
      (acc, [lang, fields]) => {
        const field_url_label = fields.find(
          (item: any) => item.propertyName === "url_label",
        );
        if (field_url_label) {
          acc[lang] = {
            propertyName: "url_label",
            translatedValue: field_url_label.translatedValue,
          };
        }
        return acc;
      },
      {} as Record<string, { propertyName: string; translatedValue: string }>,
    );

    //CREATE PAYLOAD
    const descriptionPayload = {
      idObject: idObject,
      entityType: "useful_links",
      corporateId: id,
      propertiesTranslated: descriptions,
    };

    const urlLabelPayload = {
      idObject: idObject,
      entityType: "useful_links",
      corporateId: id,
      propertiesTranslated: urlLabels,
    };

    setSubmitState({ loading: true, submitted: false });

    try {
      await trigger(descriptionPayload);
      await trigger(urlLabelPayload);
      console.log("Submitted successfully");

      setSubmitState({ loading: false, submitted: true });
      handleLangPopupClose();
    } catch (error) {
      console.error("Error during submission:", error);
      setSubmitState({ loading: false, submitted: false });
    }
  };

  return (
    <>
      <IconButton onClick={handleLangIconClick} edge="end">
        <LanguageIcon />
      </IconButton>
      <Dialog
        open={isLangPopupOpen}
        onClose={handleLangPopupClose}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "100%",
            "@media (min-width:600px)": {
              maxWidth: "80%",
            },
            "@media (min-width:960px)": {
              maxWidth: "70%",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
            paddingRight: "16px",
          }}
        >
          <Box>
            <Typography
              sx={{ marginBottom: "10px" }}
              variant="h6"
              component="div"
            >
              {t("usefulLinks")}
            </Typography>
          </Box>
          <IconButton onClick={handleLangPopupClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!fieldStates || Object.keys(fieldStates).length <= 0 ? (
            <FormLoadingStateCst />
          ) : (
            <Grid
              container
              spacing={2}
              sx={{
                marginY: "20px",
                maxWidth: "100%",
              }}
            >
              {languages &&
                languages.secondary_languages.map((lang: any) => {
                  const fieldData = fieldStates[lang.id] || {};
                  const urlLabelValue = Array.isArray(fieldData)
                    ? fieldData.find(
                        (item: any) => item.propertyName === "url_label",
                      )?.translatedValue || ""
                    : "";
                  const descriptionValue = Array.isArray(fieldData)
                    ? fieldData.find(
                        (item: any) => item.propertyName === "description",
                      )?.translatedValue || ""
                    : "";

                  return (
                    <InputWrapper
                      label={lang.label}
                      key={`${col.accessorKey}-${lang.id}`}
                    >
                      <Grid
                        item
                        xs={12}
                        key={`${col.accessorKey}-${lang.id}`}
                        sx={{
                          display: "flex",
                          marginY: "5px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", lg: "row" },
                            gap: 2,
                            flexGrow: 1,
                          }}
                        >
                          {/* URL Label */}
                          <InputCst
                            id={`${col.accessorKey}-${lang.id}-url_label`}
                            label={t("urlLabel") || ""}
                            value={urlLabelValue}
                            onChange={(e) =>
                              handleFieldChange(
                                lang.id,
                                e.target.value,
                                "url_label",
                              )
                            }
                            multiline={col.multiline}
                            rows={col.rows}
                            sx={{ flex: "auto" }}
                          />

                          {/* Description */}
                          <InputCst
                            id={`${col.accessorKey}-${lang.id}-description`}
                            label={t("description") || ""}
                            value={descriptionValue}
                            onChange={(e) =>
                              handleFieldChange(
                                lang.id,
                                e.target.value,
                                "description",
                              )
                            }
                            multiline={col.multiline}
                            rows={col.rows}
                            sx={{ flex: "auto" }}
                          />
                        </Box>
                      </Grid>
                    </InputWrapper>
                  );
                })}
            </Grid>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {submitState.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("saveButton")
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LanguageSelectorLinks;
